import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { LocationInput } from "../types";
import { Input } from "@/components/ui/input";
import { experienceService } from "@/services/experienceService";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { debounce } from "lodash";

interface LocationStepProps {
  value: LocationInput;
  onChange: (value: LocationInput) => void;
  activityName: string;
  handleNext: (locationData?: LocationInput) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

const LocationStep: React.FC<LocationStepProps> = ({
  value,
  onChange,
  activityName,
  handleNext,
  onKeyDown,
}) => {
  const { selectedLocation } = useTripStore();
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      if (!searchValue || searchValue.length <= 1) {
        setAutocompleteResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await experienceService.searchCustomPlaces(
          searchValue,
          selectedLocation?.coordinates.latitude || 0,
          selectedLocation?.coordinates.longitude || 0
        );
        setAutocompleteResults(response.data.results || []);
      } catch (error) {
        console.error("Error fetching places:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedLocation]
  );

  const debouncedSearch = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node)
      ) {
        setAutocompleteResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddressSelect = (result: any) => {
    const locationData = {
      type: "address" as const,
      value: result.name,
      coordinates: {
        latitude: result.coordinates.latitude,
        longitude: result.coordinates.longitude,
      },
    };
    onChange(locationData);
    setAutocompleteResults([]);
    handleNext(locationData);
  };

  return (
    <div className="space-y-2 -translate-y-2">
      <div className="relative" ref={autocompleteRef}>
        <Input
          placeholder={activityName || "Enter address"}
          value={value.value}
          onChange={(e) => {
            onChange({
              type: "address",
              value: e.target.value,
              coordinates: { latitude: 0, longitude: 0 },
            });
            debouncedSearch(e.target.value);
          }}
          onKeyDown={onKeyDown}
          className="w-full text-sm font-light leading-relaxed focus-visible:outline-none focus-visible:ring-0 rounded-lg"
        />
        {(isLoading || (!isLoading && autocompleteResults.length > 0)) && (
          <div className="absolute left-0 right-0 mt-1">
            <div className="bg-white rounded-lg shadow border border-neutral-200 max-h-[200px] overflow-y-auto no-scrollbar relative">
              {isLoading ? (
                // Loading skeletons
                <div className="divide-y divide-neutral-100">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="p-3 animate-pulse">
                      <div className="h-4 bg-neutral-100 rounded w-3/4 mb-1.5"></div>
                      <div className="h-3 bg-neutral-100 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                // Results list
                <div className="divide-y divide-neutral-100">
                  {autocompleteResults.slice(0, 5).map((result, index) => (
                    <div
                      key={index}
                      onClick={() => handleAddressSelect(result)}
                      className="p-2 hover:bg-neutral-50 cursor-pointer transition-colors"
                    >
                      <div className="font-medium text-xs text-neutral-800 mb-0.5">
                        {result.name}
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        {result.address}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationStep;
